import { useApolloClient, useMutation } from "@apollo/client";
import {
  Button,
  Center,
  Checkbox,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spacer,
  Spinner,
  Stack,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  AdminCreateUserMutation,
  AdminCreateUserMutationVariables,
  UserAttributeFragment,
  UserListQuery,
  UserRoleType,
} from "gql/graphql";
import USER_LIST_QUERY from "modules/providers/users-provider/networking/user-list";
import Toast from "modules/toasts/toast";
import React, { memo, useCallback, useEffect, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import {
  AdminKeyIcon,
  AdminUserPlusIcon,
  ManageAccountsIcon,
  GearIcon,
  InfoIcon,
  PersonCircleIcon,
} from "modules/icons";
import ADMIN_CREATE_USER_MUTATION from "./_networking/admin-create-user";
import { useOverlay } from "modules/providers/overlay-provider";
import useDisplayToast from "modules/hooks/use-display-toast";
import { CALLOUT_KEY } from "modules/utils/Constants";
import AdminUserAttributesSelector from "./admin-user/admin-user-attributes-selector";
import { isEmpty } from "lodash";
import { useUserAttributes } from "modules/providers/user-attributes-provider";
import {
  isValidPhoneNumberWithAreaCode,
  getMessageFromError,
} from "modules/utils/Functions";
import { supportedTranslationLanguages } from "modules/utils/translation";

const AdminAddUserDrawer = () => {
  const { cache } = useApolloClient();
  const { userAttributeCategories } = useUserAttributes();
  const { displayToast } = useDisplayToast();
  const { closeDrawer } = useOverlay();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attributes, setAttributes] = useState<UserAttributeFragment[]>([]);

  const [adminCreateUserMutationAPI] = useMutation<
    AdminCreateUserMutation,
    AdminCreateUserMutationVariables
  >(ADMIN_CREATE_USER_MUTATION);

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    setError,
    setValue,
    clearErrors,
    watch,
  } = useForm({
    mode: "onSubmit",
  });

  const email: string | undefined = watch("email");
  const phone: string | undefined = watch("phone");
  const isPhoneInvalid =
    Boolean(errors.phone) || !phone || !isValidPhoneNumberWithAreaCode(phone);

  useEffect(() => {
    if (isPhoneInvalid) {
      setValue("isSms", false);
    }
  }, [isPhoneInvalid, setValue]);

  const onSubmit = useCallback(
    async ({
      firstName,
      lastName,
      callout,
      locale,
      email,
      phone,
      isAdmin,
      isAllDistributionGroups,
      isSms,
      isHideEmail,
      isHidePhone,
    }: FieldValues) => {
      if (!email && !phone) {
        setError("email", {
          type: "required",
          message: "A phone number or email is required",
        });
        setError("phone", {
          type: "required",
          message: "A phone number or email is required",
        });
        return;
      }
      setIsSubmitting(true);
      adminCreateUserMutationAPI({
        variables: {
          input: {
            firstName,
            lastName,
            callout,
            email,
            phone,
            roleType: isAdmin ? UserRoleType.Admin : UserRoleType.User,
            isAllDistributionGroups,
            isHideEmail,
            isHidePhone,
            userAttributeIdList: attributes.map(
              (userAttribute) => userAttribute.id,
            ),
            userPreferenceList: [
              {
                name: "LOCALE",
                value: locale,
              },
              {
                name: "SMS",
                value: isSms === true ? "true" : "false",
              },
            ],
          },
        },
        onCompleted: (data) => {
          const user = data?.adminCreateUser;
          if (!user) {
            return;
          }
          cache.updateQuery<UserListQuery>(
            {
              query: USER_LIST_QUERY,
            },
            (data) => {
              if (!data) {
                return data;
              }
              return {
                ...data,
                userList: [...data.userList, user],
              };
            },
          );
          closeDrawer();
          displayToast(() => (
            <Toast
              message="New User Added"
              leftIcon={
                <AdminUserPlusIcon boxSize={5} fill={colors.grayMain} />
              }
            />
          ));
        },
        onError: (error: Error) => {
          setIsSubmitting(false);
          setError("root", {
            message: error.message,
          });
        },
      });
    },
    [
      adminCreateUserMutationAPI,
      attributes,
      setError,
      cache,
      closeDrawer,
      displayToast,
    ],
  );

  return (
    <form id="admin-add-user-form" onSubmit={handleSubmit(onSubmit)}>
      <DrawerContent>
        <DrawerCloseButton left={8} top={8} color={colors.gray30} size="lg" />
        <DrawerHeader pt={8}>
          <Center fontFamily="Outfit" fontSize={26} fontWeight={700}>
            Add User
          </Center>
        </DrawerHeader>
        <DrawerBody p={0}>
          <Stack spacing={8} px={8} pb={16}>
            <VStack spacing={4} w="full" alignItems="flex-start">
              <HStack
                w="full"
                borderBottomWidth={1}
                borderBottomColor={colors.black}
                pb={2}
              >
                <InfoIcon boxSize={6} />
                <Text fontWeight={600} fontSize={18}>
                  User Info
                </Text>
              </HStack>
              <FormControl isInvalid={!!errors?.firstName}>
                <FormLabel color={colors.blackSecondary} fontWeight={700}>
                  First Name
                </FormLabel>
                <Input
                  id="firstName"
                  {...register("firstName", {
                    required: {
                      message: "The first name is required",
                      value: true,
                    },
                    maxLength: {
                      value: 60,
                      message: "The first name must be 60 characters or less",
                    },
                  })}
                />
                <FormErrorMessage>
                  {getMessageFromError(errors?.firstName?.message)}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors?.lastName}>
                <FormLabel color={colors.blackSecondary} fontWeight={700}>
                  Last Name
                </FormLabel>
                <Input
                  id="lastName"
                  {...register("lastName", {
                    required: {
                      message: "The last name is required",
                      value: true,
                    },
                    maxLength: {
                      value: 60,
                      message: "The last name must be 60 characters or less",
                    },
                  })}
                />
                <FormErrorMessage>
                  {getMessageFromError(errors?.lastName?.message)}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors?.callout}>
                <FormLabel color={colors.blackSecondary} fontWeight={700}>
                  Username
                </FormLabel>
                <InputGroup>
                  <InputLeftElement h="100%" color={colors.grayPrimary}>
                    {CALLOUT_KEY}
                  </InputLeftElement>
                  <Input
                    id="callout"
                    fontWeight={700}
                    color={colors.blackSecondary}
                    fontSize={14}
                    {...register("callout", {
                      pattern: {
                        value: /^[a-z-._]+$/,
                        message:
                          "must be lower case along with dashes, underscores and periods ",
                      },
                      required: {
                        message: "The username is required",
                        value: true,
                      },
                    })}
                  />
                </InputGroup>
                <Text color={colors.grayMain} fontSize={12}>
                  Special characters allowed: dashes, underscores and periods
                </Text>
                <FormErrorMessage>
                  {getMessageFromError(errors?.callout?.message)}
                </FormErrorMessage>
              </FormControl>
            </VStack>
            <VStack spacing={4} w="full" alignItems="flex-start">
              <VStack spacing={0} w="full" alignItems="flex-start">
                <HStack
                  w="full"
                  borderBottomWidth={1}
                  borderBottomColor={colors.black}
                  pb={2}
                >
                  <PersonCircleIcon boxSize={6} />
                  <Text fontWeight={600} fontSize={18}>
                    User Attributes
                  </Text>
                </HStack>
              </VStack>
              {userAttributeCategories.map((userAttributeCategory) => {
                const _setUserUserAttributes = (
                  newUserAttributes: UserAttributeFragment[],
                ) => {
                  //need to remove the tags in the category that are not in the new tags
                  setAttributes((prevUserAttributes) => {
                    const updatedUserAttributes = [
                      ...prevUserAttributes,
                    ].filter(
                      (userAttribute) =>
                        userAttribute.userAttributeCategoryId !==
                        userAttributeCategory.id,
                    );
                    updatedUserAttributes.push(...newUserAttributes);
                    return updatedUserAttributes;
                  });
                };
                return (
                  <VStack
                    key={userAttributeCategory.id}
                    spacing={0}
                    w="full"
                    alignItems="flex-start"
                  >
                    <Text
                      fontWeight={700}
                      fontSize={16}
                      color={colors.blackSecondary}
                    >
                      {userAttributeCategory.name}
                    </Text>
                    <AdminUserAttributesSelector
                      userAttributeCategory={userAttributeCategory}
                      onSelectedOptionsChanged={_setUserUserAttributes}
                      placeHolder="Select"
                    />
                  </VStack>
                );
              })}
            </VStack>

            <VStack w="full" alignItems="flex-start">
              <HStack
                w="full"
                borderBottomWidth={1}
                borderBottomColor={colors.black}
                pb={2}
              >
                <AdminKeyIcon boxSize={6} />
                <Text fontWeight={700}>Authentication Info</Text>
              </HStack>
              <VStack spacing={4} w="full">
                <Text color={colors.grayMain} fontSize={12} fontWeight={400}>
                  An email address or mobile phone number is required to allow
                  user to login to app
                </Text>
                <FormControl isInvalid={!!errors?.email}>
                  <HStack w="full">
                    <FormLabel
                      color={colors.blackSecondary}
                      fontWeight={700}
                      fontSize={16}
                    >
                      Email
                    </FormLabel>
                    <Spacer />
                    <Checkbox
                      id="hideEmail"
                      fontSize={14}
                      fontWeight={400}
                      color={colors.blackSecondary}
                      isDisabled={!email}
                      {...register("isHideEmail")}
                    >
                      Hide
                    </Checkbox>
                  </HStack>
                  <Input
                    id="email"
                    onFocus={() => {
                      clearErrors(["email", "phone"]);
                    }}
                    {...register("email")}
                  />
                  <FormErrorMessage>
                    {getMessageFromError(errors?.email?.message)}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors?.phone}>
                  <HStack w="full">
                    <FormLabel
                      color={colors.blackSecondary}
                      fontWeight={700}
                      fontSize={16}
                    >
                      Mobile
                    </FormLabel>
                    <Spacer />
                    <Checkbox
                      id="hidePhone"
                      fontSize={14}
                      fontWeight={400}
                      color={colors.blackSecondary}
                      isDisabled={!phone}
                      {...register("isHidePhone")}
                    >
                      Hide
                    </Checkbox>
                  </HStack>
                  <Input
                    id="phone"
                    onFocus={() => {
                      clearErrors(["email", "phone"]);
                    }}
                    {...register("phone", {
                      validate: (value) =>
                        isEmpty(value) ||
                        isValidPhoneNumberWithAreaCode(value) ||
                        "Invalid phone number",
                    })}
                  />
                  <FormErrorMessage>
                    {getMessageFromError(errors?.phone?.message)}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
            </VStack>

            <VStack w="full" alignItems="flex-start" spacing={4}>
              <HStack
                w="full"
                borderBottomWidth={1}
                borderBottomColor={colors.black}
                pb={2}
              >
                <GearIcon boxSize={6} />
                <Text fontWeight={700}>Settings</Text>
              </HStack>
              <FormControl isInvalid={!!errors?.locale}>
                <FormLabel color={colors.blackSecondary} fontWeight={700}>
                  Preferred Language
                </FormLabel>
                <InputGroup>
                  <Select
                    placeholder="Select Language"
                    fontSize={14}
                    {...register("locale", { required: false })}
                  >
                    {supportedTranslationLanguages.map(
                      ([langCode, langName]) => (
                        <option key={langCode} value={langCode}>
                          {langName}
                        </option>
                      ),
                    )}
                  </Select>
                </InputGroup>
                <FormErrorMessage>
                  {getMessageFromError(errors?.locale?.message)}
                </FormErrorMessage>
              </FormControl>
              <FormControl>
                <HStack>
                  <FormLabel color={colors.blackSecondary} fontWeight={700}>
                    SMS/Text Delivery
                  </FormLabel>
                  <Spacer />
                  <Controller
                    control={control}
                    name="isSms"
                    render={({ field }) => (
                      <Switch
                        id="isSms"
                        size="sm"
                        isChecked={field.value}
                        onChange={field.onChange}
                        isDisabled={isPhoneInvalid}
                      />
                    )}
                  />
                </HStack>
                <Text fontWeight={400} fontSize={14} color={colors.gray50}>
                  Deliver Synco messages via SMS to user's device
                </Text>
              </FormControl>
            </VStack>
            <VStack w="full" alignItems="flex-start">
              <HStack
                w="full"
                borderBottomWidth={1}
                borderBottomColor={colors.black}
                pb={2}
              >
                <ManageAccountsIcon boxSize={6} />
                <Text color={colors.blackSecondary} fontWeight={700}>
                  Advanced Settings
                </Text>
              </HStack>
              <FormControl>
                <HStack w="full">
                  <VStack w="full" spacing={0} alignItems="flex-start">
                    <Text
                      fontWeight={700}
                      fontSize={16}
                      color={colors.blackSecondary}
                    >
                      Make an Admin
                    </Text>
                    <Text
                      fontWeight={400}
                      fontSize={16}
                      color={colors.grayMain}
                    >
                      Admins can add and edit users, groups, and tags
                    </Text>
                  </VStack>
                  <Switch id="isAdmin" size="sm" {...register("isAdmin")} />
                </HStack>
              </FormControl>
              <FormControl>
                <HStack w="full">
                  <VStack w="full" spacing={0} alignItems="flex-start">
                    <Text
                      fontWeight={700}
                      fontSize={16}
                      color={colors.blackSecondary}
                    >
                      Add to all Groups
                    </Text>
                    <Text
                      fontWeight={400}
                      fontSize={16}
                      color={colors.grayMain}
                    >
                      Automatically add this user to every group in org
                    </Text>
                  </VStack>
                  <Switch
                    id="isAllDistributionGroups"
                    size="sm"
                    {...register("isAllDistributionGroups")}
                  />
                </HStack>
              </FormControl>
            </VStack>
          </Stack>
        </DrawerBody>
        <DrawerFooter h={100} boxShadow="0px -4px 4px rgba(0, 0, 0, 0.05)">
          <VStack w="full">
            {(errors.email || errors.phone) && (
              <Text color={colors.red}>
                {getMessageFromError(errors?.email?.message)}
              </Text>
            )}
            {errors.root && (
              <Text color={colors.red}>
                {getMessageFromError(errors?.root?.message)}
              </Text>
            )}
            <Center w="full">
              <HStack spacing={12}>
                <Button
                  variant="cancel"
                  h="40px"
                  w="140px"
                  onClick={(e) => {
                    clearErrors("root");
                    e.stopPropagation();
                    closeDrawer();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  h="40px"
                  w="140px"
                  type="submit"
                  onClick={() => {
                    clearErrors("root");
                  }}
                >
                  {isSubmitting ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Center>
          </VStack>
        </DrawerFooter>
      </DrawerContent>
    </form>
  );
};

export default memo(AdminAddUserDrawer);
